import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import { StockItem } from "@/types/stock-item";
import { Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface StockItemSettingsTabProps {
  item: StockItem;
  isEditMode: boolean;
  onItemChange: (item: StockItem) => void;
}

export function StockItemSettingsTab({ item, isEditMode, onItemChange }: StockItemSettingsTabProps) {
  const handleFieldChange = (field: keyof StockItem, value: any) => {
    const updatedItem = { ...item, [field]: value };
    onItemChange(updatedItem);
  };

  // Type options
  const typeOptions: ComboboxOption[] = [
    { 
      value: "Purchased", 
      label: "Purchased",
      description: "Standard item bought from suppliers"
    },
    { 
      value: "Manufactured", 
      label: "Manufactured",
      description: "Standard item produced in-house"
    },
    { 
      value: "Serial Number", 
      label: "Serial Number",
      description: "Each unit has unique serial number"
    },
    { 
      value: "Assembly", 
      label: "Assembly",
      description: "Built from components (BOM)"
    },
    { 
      value: "Component", 
      label: "Component",
      description: "Used in assemblies"
    },
    { 
      value: "Template", 
      label: "Template",
      description: "Master item for creating variants"
    },
  ];

  return (
    <div className="space-y-6">
      {/* Item Type */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold mb-1">Type</h3>
          <p className="text-xs text-muted-foreground">
            Define how this item is tracked and sourced
          </p>
        </div>
        <Separator />
        
        <div className="space-y-2">
          <Label htmlFor="type" className="flex items-center gap-1">
            Type <span className="text-destructive">*</span>
          </Label>
          <Combobox
            options={typeOptions}
            value={item.type || "Purchased"}
            onValueChange={(value) => handleFieldChange("type", value)}
            placeholder="Select type..."
            searchPlaceholder="Search types..."
            emptyText="No type found."
            disabled={!isEditMode}
          />
          <p className="text-xs text-muted-foreground">
            Determines tracking method and procurement approach
          </p>
        </div>
      </div>

      {/* Transaction Settings */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold mb-1">Transaction Settings</h3>
          <p className="text-xs text-muted-foreground">
            Control whether this item can be sold or purchased
          </p>
        </div>
        <Separator />
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="isSellable">Can be sold to customers</Label>
              <p className="text-xs text-muted-foreground">
                Item will appear in sales orders and POS
              </p>
            </div>
            <Switch
              id="isSellable"
              checked={false}
              onCheckedChange={(checked) => handleFieldChange("isSellable", checked)}
              disabled={true}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="isPurchasable">Can be purchased from suppliers</Label>
              <p className="text-xs text-muted-foreground">
                Item will appear in purchase orders
              </p>
            </div>
            <Switch
              id="isPurchasable"
              checked={false}
              onCheckedChange={(checked) => handleFieldChange("isPurchasable", checked)}
              disabled={true}
            />
          </div>
        </div>
      </div>

      {/* Assembly Settings */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold mb-1">Assembly Settings</h3>
          <p className="text-xs text-muted-foreground">
            Configure Bill of Materials (BOM) options
          </p>
        </div>
        <Separator />
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="isAssembly">Can be assembled from components</Label>
              <p className="text-xs text-muted-foreground">
                Enable BOM and assembly movements
              </p>
            </div>
            <Switch
              id="isAssembly"
              checked={false}
              onCheckedChange={(checked) => handleFieldChange("isAssembly", checked)}
              disabled={true}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="isComponent">Can be used in other assemblies</Label>
              <p className="text-xs text-muted-foreground">
                Item will appear in BOM component picker
              </p>
            </div>
            <Switch
              id="isComponent"
              checked={false}
              onCheckedChange={(checked) => handleFieldChange("isComponent", checked)}
              disabled={true}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="allowDisassembly">Allow disassembly</Label>
              <p className="text-xs text-muted-foreground">
                Permit breaking down assemblies into components
              </p>
            </div>
            <Switch
              id="allowDisassembly"
              checked={false}
              onCheckedChange={(checked) => handleFieldChange("allowDisassembly", checked)}
              disabled={true}
            />
          </div>
        </div>
      </div>

      {/* Tracking Configuration */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold mb-1">Tracking Configuration</h3>
          <p className="text-xs text-muted-foreground">
            Enable lot, batch, or serial number tracking
          </p>
        </div>
        <Separator />
        
        <div className="space-y-4">
          <div className="space-y-3">
            <Label>Tracking Type</Label>
            <RadioGroup
              value="None"
              onValueChange={(value) => handleFieldChange("trackingType", value)}
              className="space-y-3"
            >
              <div className="flex items-start space-x-3">
                <RadioGroupItem value="None" id="tracking-none" disabled={!isEditMode} />
                <div className="space-y-0.5">
                  <Label htmlFor="tracking-none" className="font-normal cursor-pointer">
                    None
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Standard inventory without tracking
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <RadioGroupItem value="Lot" id="tracking-lot" disabled={true} />
                <div className="space-y-0.5">
                  <Label htmlFor="tracking-lot" className="font-normal cursor-pointer">
                    Lot Number
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Track by supplier lot numbers (e.g., raw materials)
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <RadioGroupItem value="Batch" id="tracking-batch" disabled={true} />
                <div className="space-y-0.5">
                  <Label htmlFor="tracking-batch" className="font-normal cursor-pointer">
                    Batch Number
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Track by internal production batches (e.g., pharmaceuticals, food)
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <RadioGroupItem value="Serial" id="tracking-serial" disabled={true} />
                <div className="space-y-0.5">
                  <Label htmlFor="tracking-serial" className="font-normal cursor-pointer">
                    Serial Number
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Track individual units (e.g., electronics, equipment)
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Tracking Options - Only show if tracking is enabled */}
          {item.trackingType && item.trackingType !== "None" && (
            <div className="space-y-4 pl-7 border-l-2 border-muted">
              {item.trackingType === "Serial" && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="autoGenerateSerial">Auto-generate serial numbers</Label>
                      <p className="text-xs text-muted-foreground">
                        Automatically create sequential serial numbers
                      </p>
                    </div>
                    <Switch
                      id="autoGenerateSerial"
                      checked={item.autoGenerateSerial ?? false}
                      onCheckedChange={(checked) => handleFieldChange("autoGenerateSerial", checked)}
                      disabled={!isEditMode}
                    />
                  </div>

                  {item.autoGenerateSerial && (
                    <div className="space-y-2">
                      <Label htmlFor="serialPrefix">Serial number prefix</Label>
                      <Input
                        id="serialPrefix"
                        value={item.serialPrefix || ""}
                        onChange={(e) => handleFieldChange("serialPrefix", e.target.value)}
                        placeholder="e.g., LAPTOP-"
                        readOnly={!isEditMode}
                        className={`${!isEditMode ? 'bg-muted border-0 cursor-default' : ''}`}
                      />
                      <p className="text-xs text-muted-foreground">
                        Example: LAPTOP-0001, LAPTOP-0002, etc.
                      </p>
                    </div>
                  )}
                </>
              )}

              {(item.trackingType === "Batch" || item.trackingType === "Lot") && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="requireExpiryDate">Require expiry date</Label>
                      <p className="text-xs text-muted-foreground">
                        Mandate expiry date for each {item.trackingType.toLowerCase()}
                      </p>
                    </div>
                    <Switch
                      id="requireExpiryDate"
                      checked={item.requireExpiryDate ?? false}
                      onCheckedChange={(checked) => handleFieldChange("requireExpiryDate", checked)}
                      disabled={!isEditMode}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="requireManufacturingDate">Require manufacturing date</Label>
                      <p className="text-xs text-muted-foreground">
                        Mandate manufacturing date for each {item.trackingType.toLowerCase()}
                      </p>
                    </div>
                    <Switch
                      id="requireManufacturingDate"
                      checked={item.requireManufacturingDate ?? false}
                      onCheckedChange={(checked) => handleFieldChange("requireManufacturingDate", checked)}
                      disabled={!isEditMode}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="shelfLifeDays">Shelf life (days)</Label>
                    <Input
                      id="shelfLifeDays"
                      type="number"
                      value={item.shelfLifeDays || ""}
                      onChange={(e) => handleFieldChange("shelfLifeDays", e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder="e.g., 365"
                      readOnly={!isEditMode}
                      className={`${!isEditMode ? 'bg-muted border-0 cursor-default' : ''}`}
                    />
                    <p className="text-xs text-muted-foreground">
                      Used to calculate expiry date from manufacturing date
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Inventory Settings */}
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold mb-1">Inventory Settings</h3>
          <p className="text-xs text-muted-foreground">
            Configure inventory control parameters
          </p>
        </div>
        <Separator />
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="allowNegativeStock">Allow negative stock</Label>
              <p className="text-xs text-muted-foreground">
                Permit stock levels to go below zero
              </p>
            </div>
            <Switch
              id="allowNegativeStock"
              checked={item.allowNegativeStock ?? false}
              onCheckedChange={(checked) => handleFieldChange("allowNegativeStock", checked)}
              disabled={!isEditMode}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="reorderLevel">Reorder level</Label>
              <Input
                id="reorderLevel"
                type="number"
                value={item.reorderPoint || ""}
                onChange={(e) => handleFieldChange("reorderPoint", e.target.value ? parseFloat(e.target.value) : 0)}
                placeholder="0"
                readOnly={!isEditMode}
                className={`${!isEditMode ? 'bg-muted border-0 cursor-default' : ''}`}
              />
              <p className="text-xs text-muted-foreground">
                Alert when stock falls below this level
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reorderQuantity">Reorder quantity</Label>
              <Input
                id="reorderQuantity"
                type="number"
                value={item.reorderQuantity || ""}
                onChange={(e) => handleFieldChange("reorderQuantity", e.target.value ? parseFloat(e.target.value) : 0)}
                placeholder="0"
                readOnly={!isEditMode}
                className={`${!isEditMode ? 'bg-muted border-0 cursor-default' : ''}`}
              />
              <p className="text-xs text-muted-foreground">
                Suggested quantity to reorder
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
